"use client";
import { useRef, useCallback } from "react";
import { useAppSelector, useAppDispatch } from "@client/store/store.hooks";
import { selectZoomLevel, zoomIn, zoomOut } from "@client/store/slices/ui.slice";
import { Logger } from "@shared/utils/logger";

/**
 * Custom hook for managing zoom state and drag-to-pan behavior.
 * Encapsulates all zoom-related logic for cleaner component code.
 */
export function useZoom() {
  const dispatch = useAppDispatch();
  const zoomLevel = useAppSelector(selectZoomLevel);

  // Refs for Drag/Pan logic
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const scrollLeft = useRef(0);
  const scrollTop = useRef(0);

  const handleZoomIn = useCallback(() => {
    dispatch(zoomIn());
  }, [dispatch]);

  const handleZoomOut = useCallback(() => {
    dispatch(zoomOut());
  }, [dispatch]);

  const handleFullScreen = useCallback(() => {
    if (scrollContainerRef.current) {
      if (!document.fullscreenElement) {
        scrollContainerRef.current.requestFullscreen().catch((err: Error) => {
          Logger.error("useZoom", "Error attempting to enable fullscreen:", err);
        });
      } else {
        document.exitFullscreen();
      }
    }
  }, []);

  // Drag Handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoomLevel <= 1 || !scrollContainerRef.current) return;
      isDragging.current = true;
      startX.current = e.pageX - scrollContainerRef.current.offsetLeft;
      startY.current = e.pageY - scrollContainerRef.current.offsetTop;
      scrollLeft.current = scrollContainerRef.current.scrollLeft;
      scrollTop.current = scrollContainerRef.current.scrollTop;
      e.preventDefault(); // Prevent text selection
    },
    [zoomLevel]
  );

  const handleMouseLeave = useCallback(() => {
    isDragging.current = false;
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const y = e.pageY - scrollContainerRef.current.offsetTop;
    const walkX = (x - startX.current) * 1; // Scroll speed multiplier
    const walkY = (y - startY.current) * 1;
    scrollContainerRef.current.scrollLeft = scrollLeft.current - walkX;
    scrollContainerRef.current.scrollTop = scrollTop.current - walkY;
  }, []);

  return {
    zoomLevel,
    scrollContainerRef,
    handleZoomIn,
    handleZoomOut,
    handleFullScreen,
    handleMouseDown,
    handleMouseLeave,
    handleMouseUp,
    handleMouseMove
  };
}
